import axios from 'axios';
import { useEffect, useState } from 'react';
import API_URL from '../config/api';
import './CurrentBalance.css';

const CurrentBalance = ({ user }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentBalance, setCurrentBalance] = useState(0);

  useEffect(() => {
    if (user) {
      setCurrentBalance(user.pointsBalance || 0);
      fetchTransactions();
    }
  }, [user]);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Backend returns { transactions: [...], pagination: {...}, summary: {...} }
      const transactionsData = response.data.transactions || [];

      // Sort transactions by date (newest first) - they should already be sorted from backend
      const sortedTransactions = transactionsData.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setTransactions(sortedTransactions);
      
      // Update current balance from the most recent transaction if available
      if (sortedTransactions.length > 0) {
        setCurrentBalance(sortedTransactions[0].balanceAfter);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Fetch transactions error:', err);
      setError(err.response?.data?.message || 'Failed to load transactions');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return Math.abs(amount).toLocaleString('en-US');
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'earned':
        return 'Earned';
      case 'spent':
        return 'Spent';
      case 'bonus':
        return 'Bonus';
      case 'refund':
        return 'Refund';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Current Balance & Ledger</h1>
          <p>View your account balance and complete transaction history</p>
        </div>
        <div className="skeleton-summary">
          <div className="skeleton-card">
            <div className="skeleton-line short"></div>
            <div className="skeleton-line medium"></div>
            <div className="skeleton-line medium"></div>
          </div>
        </div>
        <div className="skeleton-table">
          <div className="skeleton-table-header">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="skeleton-line short"></div>)}
          </div>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="skeleton-table-row">
              <div className="skeleton-line medium"></div>
              <div className="skeleton-line long"></div>
              <div className="skeleton-line short"></div>
              <div className="skeleton-line short"></div>
              <div className="skeleton-line short"></div>
              <div className="skeleton-line short"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Current Balance & Ledger</h1>
        <p>View your account balance and complete transaction history</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Balance Summary Cards */}
      <div className="balance-summary">
        <div className="balance-card stats">
          <table className="stats-table">
            <thead>
              <tr>
                <th>Total Transactions</th>
                <th>Total Earned</th>
                <th>Total Redeemed</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="stat-value">{transactions.length}</td>
                <td className="stat-value positive">
                  +{transactions
                    .filter(t => t.type === 'earned' || t.type === 'bonus' || t.type === 'refund')
                    .reduce((sum, t) => sum + t.amount, 0)
                    .toLocaleString('en-US')}
                </td>
                <td className="stat-value negative">
                  -{transactions
                    .filter(t => t.type === 'spent')
                    .reduce((sum, t) => sum + t.amount, 0)
                    .toLocaleString('en-US')}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="ledger-section">
        <div className="ledger-header">
          <div className="ledger-title">
            <h2>Transaction Ledger</h2>
            <p className="ledger-subtitle">Complete history of all your transactions</p>
          </div>
          <div className="ledger-balance">
            <div className="balance-label">Current Balance</div>
            <div className="balance-value">
              {(currentBalance / 10000) >= 0.01 && `$${(currentBalance / 10000).toFixed(2)} `}
              ({currentBalance.toLocaleString('en-US')} pts)
            </div>
          </div>
        </div>

        {transactions.length === 0 ? (
          <div className="no-transactions">
            <div className="no-transactions-icon">📊</div>
            <h3>No Transactions Yet</h3>
            <p>Complete surveys to start earning and see your transaction history here.</p>
          </div>
        ) : (
          <div className="ledger-table-container">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th className="text-right">Debit</th>
                  <th className="text-right">Credit</th>
                  <th className="text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => {
                  const isCredit = transaction.type === 'earned' || transaction.type === 'bonus' || transaction.type === 'refund';
                  const isDebit = transaction.type === 'spent';
                  
                  return (
                    <tr key={transaction._id} className={index % 2 === 0 ? 'even' : 'odd'}>
                      <td className="date-cell">{formatDate(transaction.createdAt)}</td>
                      <td className="description-cell">{transaction.description}</td>
                      <td>
                        <span className={`type-badge type-${transaction.type}`}>
                          {getTransactionTypeLabel(transaction.type)}
                        </span>
                      </td>
                      <td className="text-right debit-cell">
                        {isDebit ? (
                          <span className="amount negative">-{formatAmount(transaction.amount)}</span>
                        ) : (
                          <span className="amount">-</span>
                        )}
                      </td>
                      <td className="text-right credit-cell">
                        {isCredit ? (
                          <span className="amount positive">+{formatAmount(transaction.amount)}</span>
                        ) : (
                          <span className="amount">-</span>
                        )}
                      </td>
                      <td className="text-right balance-cell">
                        <span className="balance-amount">
                          {transaction.balanceAfter.toLocaleString('en-US')}
                        </span>
                        <span className="balance-currency">Points</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentBalance;

